import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ShellIdentity } from '../../core/shell-identity';
import type { ConnState } from '../managers/controller-manager';
import type { FleetManager } from '../managers/fleet-manager';
import { gatewayLabel, type GatewayEntry } from '../managers/gateway-entry';
import { connViaLabel } from '../managers/transport-manager';
import { gatewayNetBadges } from './gateway-net-badges';
import type { FleetGateway } from '../../core/bridge/connect-client';
import type { Controller } from '../../core/domain/controller';
import type { Camera } from '../../core/domain/camera';
import type { CameraStreamManager } from '../components/camera/camera-stream-manager';
import type { ModelView } from '../../core/domain/model';
import type { BackendClient } from '../bridge/backend-client';
import type { StreamFrame } from '../../core/bridge/types';
import type { DescriptionRow } from '../../workers/backend.worker';
import { overviewCard, statusBadge, statusColor, statusText, pillTabBar, baseGrid, type PillTab } from '@vitesse/base-ui/components/templates';
import { button, iconButton } from '@vitesse/base-ui/components/base-buttons';
import { entryConnState } from '../managers/conn-state';
import '../components/dashboard/dashboard-card';
import { modelOverviewCard } from '../components/model/model-card';
import '../components/integration/integration-card';
import type { Integration } from '../../core/domain/integration';
import type { DashboardSummary } from '../../core/domain/dashboard';
import '../components/controller/controller-card';
import '../components/camera/camera-card';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/vitesse-icon';

const CHIP_KEYS = ['chipGateways', 'chipModel', 'chipSymbols', 'chipCapabilities'] as const;

/*
 * <vitesse-home> — React→Lit port of the agent's HomePage. LIGHT DOM so the
 * agent's _home-page.scss styles it identically. AI-first hero (greeting +
 * prompt bar + chips) over the entity grid with the pill tab bar. MULTI-GATEWAY:
 * a gateway card per org gateway (EVERY one — even disconnected), plus its
 * controllers/models flattened across the fleet. Pure renderer of `gateways`
 * (the shell owns the managers + hands a fresh ref on change); only the fleet
 * (nearby-unclaimed) is subscribed here, for the discover-and-claim banner.
 */
type HomeTab = 'all' | 'gateways' | 'controllers' | 'integrations' | 'models' | 'cameras' | 'dashboards';

@customElement('vitesse-home')
export class VitesseHome extends LitElement {
  @property({ attribute: false }) identity?: ShellIdentity;
  /** The org's gateways (roster + live managers). Fresh array ref on any manager
   *  change → home re-renders, reading `entry.controllers.all` etc. live. */
  @property({ attribute: false }) gateways: GatewayEntry[] = [];
  @property({ attribute: false }) fleet?: FleetManager;
  @property({ attribute: false }) backend?: BackendClient;
  /** The app-global camera media plane (shared PCs/streams), threaded into the
   *  camera-card previews so the home Cameras filter shows live thumbnails. */
  @property({ attribute: false }) cameraStreams?: CameraStreamManager;
  @state() private firstName = '';
  @state() private tab: HomeTab = 'gateways'; // land on the relays, not the everything-pile
  // Discover-and-claim banner state (agent's UnclaimedGatewayBanner): the boxes
  // found on the user's network, plus per-box dismissed/claiming sets.
  @state() private nearby: FleetGateway[] = [];
  @state() private dismissed = new Set<string>();
  @state() private claiming = new Set<string>();
  // Per-controller descriptions (user_data) across the WHOLE fleet — shown on
  // the controller cards in place of the raw protocol, the same data the gateway
  // detail page shows. Keyed gw → (controllerId → description); one tiny snapshot
  // stream per gateway, reconciled as the gateway set changes.
  @state() private descriptions = new Map<string, Map<string, string>>();
  private descPorts = new Map<string, MessagePort>();
  private descInFlight = new Set<string>();

  constructor() {
    super();
    new LocaleController(this); // re-render on language change
  }

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  private readonly onFleetChange = () => {
    const next = this.fleet?.nearby ?? [];
    // A box reappearing clears its dismissed flag so it can be claimed again.
    const present = new Set(next.map((g) => g.deviceFingerprint));
    for (const fp of [...this.dismissed]) if (present.has(fp)) this.dismissed.delete(fp);
    this.nearby = next;
  };
  connectedCallback(): void {
    super.connectedCallback();
    this.firstName = this.identity?.user?.firstName ?? '';
    this.fleet?.addEventListener('change', this.onFleetChange);
    this.onFleetChange();
    this.reconcileDescriptions();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.fleet?.removeEventListener('change', this.onFleetChange);
    this.closeAllDescriptions();
  }

  // One description snapshot stream per org gateway (the cards span the fleet).
  // Reconcile on every `gateways`/`backend` change: open ports for new gateways,
  // close ports for gateways that dropped out. An in-flight guard prevents a
  // re-entrant reconcile from double-opening the same gateway.
  private reconcileDescriptions(): void {
    if (!this.backend) return;
    const want = new Set(this.gateways.map((e) => e.gateway.deviceFingerprint));
    for (const [gw, port] of this.descPorts) {
      if (!want.has(gw)) {
        port.postMessage({ kind: 'close' });
        port.close();
        this.descPorts.delete(gw);
        this.descriptions.delete(gw);
      }
    }
    for (const gw of want) if (!this.descPorts.has(gw)) void this.openDescriptions(gw);
  }
  private async openDescriptions(gw: string): Promise<void> {
    if (!this.backend || this.descPorts.has(gw) || this.descInFlight.has(gw)) return;
    this.descInFlight.add(gw);
    try {
      const port = await this.backend.api.subscribeControllerDescriptions(gw);
      // The element may have disconnected, or the gateway dropped out, during the
      // await — bail and close the orphaned port rather than leaking it.
      if (!this.isConnected || !this.gateways.some((e) => e.gateway.deviceFingerprint === gw)) {
        port.close();
        return;
      }
      this.descPorts.set(gw, port);
      port.onmessage = (e: MessageEvent<StreamFrame<DescriptionRow>>) => {
        const frame = e.data;
        if (frame.kind !== 'snapshot') return;
        const inner = new Map(frame.items.map((d) => [d.controllerId, d.description]));
        this.descriptions = new Map(this.descriptions).set(gw, inner); // new ref → re-render
      };
    } finally {
      this.descInFlight.delete(gw);
    }
  }
  private closeAllDescriptions(): void {
    for (const port of this.descPorts.values()) {
      port.postMessage({ kind: 'close' });
      port.close();
    }
    this.descPorts.clear();
    this.descInFlight.clear();
    this.descriptions = new Map();
  }

  // Only the fleet manager is subscribed here (for the claim banner). The gateway
  // ENTRIES' managers are owned by the shell, which hands a fresh `gateways` ref
  // on change — so re-wire only the fleet listener when that property lands.
  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('fleet')) {
      (changed.get('fleet') as FleetManager | undefined)?.removeEventListener('change', this.onFleetChange);
      this.fleet?.addEventListener('change', this.onFleetChange);
      this.onFleetChange();
    }
    // A backend swap (or late backend) drops every description stream; a gateway
    // set change opens/closes per-gateway streams to match.
    if (changed.has('backend')) this.closeAllDescriptions();
    if (changed.has('backend') || changed.has('gateways')) this.reconcileDescriptions();
  }

  // ── flattened fleet views (read live from each entry's managers) ────────────
  // Sub-entities of a gateway we're NOT connected to are hidden — a disconnected
  // or still-connecting gateway shows only its card (with the Connect control),
  // never stale controllers/models. The gateway cards themselves always render.
  private get allControllers(): { e: GatewayEntry; c: Controller }[] {
    return this.gateways
      .filter((e) => this.effectiveConnState(e) === 'connected')
      .flatMap((e) => e.controllers.all.map((c) => ({ e, c })));
  }
  private get allCameras(): { e: GatewayEntry; c: Camera }[] {
    return this.gateways
      .filter((e) => this.effectiveConnState(e) === 'connected')
      .flatMap((e) => e.cameras.all.map((c) => ({ e, c })));
  }
  private get allModels(): { e: GatewayEntry; m: ModelView }[] {
    return this.gateways
      .filter((e) => this.effectiveConnState(e) === 'connected')
      .flatMap((e) => e.models.all.map((m) => ({ e, m })));
  }
  private get allDashboards(): { e: GatewayEntry; d: DashboardSummary }[] {
    return this.gateways
      .filter((e) => this.effectiveConnState(e) === 'connected')
      .flatMap((e) => e.dashboards.all.map((d) => ({ e, d })));
  }
  private get allIntegrations(): { e: GatewayEntry; i: Integration }[] {
    return this.gateways
      .filter((e) => this.effectiveConnState(e) === 'connected')
      .flatMap((e) => e.integrations.all.map((i) => ({ e, i })));
  }

  /*
   * WHAT WE ARE DELIBERATELY NOT SHOWING, so the empty state can say it.
   *
   * Hiding a disconnected relay's sub-entities is right — stale controllers and dead dashboard
   * tiles are worse than absence. What was wrong is what the empty tab then SAID: "No Dashboards
   * yet", to somebody with two. The relay's manager still holds them, so the count is here to be
   * had; only the assertion was invented.
   *
   * Named relays, not just a number: "2 on relays that are not connected" leaves the reader hunting,
   * and the whole point is to send them somewhere.
   */
  private hiddenOnDisconnected(tab: HomeTab): { count: number; relays: string[] } {
    const of = (e: GatewayEntry): number =>
      tab === 'controllers'
        ? e.controllers.all.length
        : tab === 'cameras'
          ? e.cameras.all.length
          : tab === 'models'
            ? e.models.all.length
            : tab === 'dashboards'
              ? e.dashboards.all.length
              : tab === 'integrations'
                ? e.integrations.all.length
                : 0;
    let count = 0;
    const relays: string[] = [];
    for (const e of this.gateways) {
      if (this.effectiveConnState(e) === 'connected') continue;
      const n = of(e);
      if (!n) continue;
      count += n;
      // The same name the card shows, so the message points at something the reader can see.
      relays.push(e.gateway.displayName || e.gateway.hostname || e.gateway.deviceFingerprint.slice(0, 12));
    }
    return { count, relays };
  }

  /** Claim a discovered box (code-free LAN-presence proof). On success the org
   *  owns it → tell the shell so it re-resolves the fleet (the card appears). */
  private async claimNearby(g: FleetGateway): Promise<void> {
    if (!this.fleet || this.claiming.has(g.deviceFingerprint)) return;
    this.claiming = new Set(this.claiming).add(g.deviceFingerprint);
    try {
      await this.fleet.claim(g.deviceFingerprint, g.displayName || g.hostname || '');
      this.dismissed = new Set(this.dismissed).add(g.deviceFingerprint);
      this.dispatchEvent(new CustomEvent('gateway-claimed', {
        detail: g.deviceFingerprint, bubbles: true, composed: true,
      }));
    } catch {
      /* error surfaces via the fleet manager; the row stays claimable */
    } finally {
      const c = new Set(this.claiming); c.delete(g.deviceFingerprint); this.claiming = c;
    }
  }

  private dismissNearby(fp: string): void {
    this.dismissed = new Set(this.dismissed).add(fp);
  }

  /** The "a gateway was detected on your network" prompt — a bright, horizontal
   *  banner per discovered box (yellow→orange gradient that pops). Hidden when
   *  nothing's discovered or every discovered box has been dismissed. */
  private claimBanner() {
    const visible = this.nearby.filter((g) => !this.dismissed.has(g.deviceFingerprint));
    if (visible.length === 0) return nothing;
    return html`
      <div class="claim-banner-stack">
        ${visible.map((g) => {
          const busy = this.claiming.has(g.deviceFingerprint);
          const label = g.displayName || g.hostname || g.deviceFingerprint.slice(0, 12);
          // Split the localized sentence around the uid so the uid fragment can
          // be styled (uppercase + italic) while the rest of the sentence stays
          // plain — the sentinel survives interpolation, then we split on it.
          const [before, after = ''] = t('fleet.detected', { uid: '\uE000' }).split('\uE000');
          return html`<div class="unclaimed-gateway-banner">
            <vitesse-icon name="hard-drive" size="18" class="unclaimed-gateway-banner__icon"></vitesse-icon>
            <span class="unclaimed-gateway-banner__title">${before}<em class="unclaimed-gateway-banner__uid">${label}</em>${after}</span>
            <span class="unclaimed-gateway-banner__spacer"></span>
            ${button({
              label: busy ? t('fleet.claiming') : t('fleet.claim'),
              icon: 'fingerprint',
              color: 'blue',
              disabled: busy,
              onClick: () => this.claimNearby(g),
            })}
            ${iconButton({ icon: 'x', title: t('notifications.dismiss'), onClick: () => this.dismissNearby(g.deviceFingerprint) })}
          </div>`;
        })}
      </div>
    `;
  }

  private nav(detail: {
    view: 'gateway' | 'controller' | 'model' | 'dashboard';
    gatewayId?: string;
    controllerId?: string;
    modelId?: string;
    dashboardId?: string;
  }): void {
    this.dispatchEvent(new CustomEvent('navigate', { detail, bubbles: true, composed: true }));
  }

  private selectTab(id: HomeTab): void {
    if (id === this.tab) return;
    this.tab = id;
    // Deliberately NO scroll on filter change — selecting a filter must never
    // move the viewport (frontend-philosophy: no unpurposeful layout/scroll
    // shift). The user's scroll position is preserved.
  }

  private get tabs(): PillTab[] {
    return [
      { id: 'all', label: t('nav.all') },
      { id: 'gateways', label: t('nav.gateways'), icon: 'hard-drive', count: this.gateways.length },
      { id: 'controllers', label: t('nav.controllers'), icon: 'cpu', count: this.allControllers.length },
      { id: 'models', label: t('nav.models'), icon: 'git-compare-arrows', count: this.allModels.length },
      { id: 'integrations', label: t('nav.integrations'), icon: 'database-arrow-up', count: this.allIntegrations.length },
      { id: 'cameras', label: t('nav.cameras'), icon: 'camera', count: this.allCameras.length },
      { id: 'dashboards', label: t('nav.dashboards'), icon: 'bar-chart-3', count: this.allDashboards.length },
    ];
  }

  // The data-plane path + RTT badge — "LAN · 12ms" (local, fast) vs "Private
  // server · 45ms" (cloud). Only when the gateway is genuinely connected (the
  // path is meaningless otherwise).
  private connViaBadge(e: GatewayEntry) {
    if (this.effectiveConnState(e) !== 'connected') return nothing;
    return statusBadge('blue', connViaLabel(e.transport.transportInfo)); // info, not a critical-status color
  }

  // The badge's tri-state — intent + data-plane liveness + (on cloud) presence.
  // Shared with the detail page + tree so all three always agree (see conn-state).
  private effectiveConnState(e: GatewayEntry): ConnState {
    return entryConnState(e);
  }

  // Frontend connect/disconnect control — the SAME 3-state spelling on cards +
  // the gateway detail page: disconnected → blue "Connect" (plug-zap); connecting
  // → yellow "Cancel" (refresh-cw-off, aborts the attempt); connected → red-on-
  // hover "Disconnect" (unplug). Frontend-only (detaches OUR streams; the box is
  // untouched). stopPropagation so it doesn't navigate into the clickable card.
  private connButton(e: GatewayEntry) {
    const st = this.effectiveConnState(e);
    const onClick = () => (e.connection.intent === 'disconnected' ? e.connection.connect() : e.connection.disconnect());
    const o =
      st === 'disconnected'
        ? { label: t('gateway.connect'), icon: 'plug-zap', color: 'blue' as const }
        : // 'offline' shares 'connecting''s control, not the fallthrough's. The box is
          // off, but our intent is still connected and supervise() is still retrying —
          // so the honest action is "stop trying" (Cancel), and the click does exactly
          // that (onClick is intent-keyed → disconnect()). Left to fall through, an
          // offline box got a red "Disconnect", which reads as "you ARE connected" on
          // a box that is switched off — the same badge/button mismatch that made the
          // Connect control lie before.
          st === 'connecting' || st === 'offline'
          ? { label: t('gateway.cancel'), icon: 'refresh-cw-off', color: 'yellow' as const }
          : { label: t('gateway.disconnect'), icon: 'unplug', color: 'red' as const };
    return html`<span @click=${(ev: Event) => ev.stopPropagation()}>${button({ ...o, size: 'sm', onClick })}</span>`;
  }

  private gatewayCard(e: GatewayEntry) {
    const gw = e.gateway.deviceFingerprint;
    const connState: ConnState = this.effectiveConnState(e);
    return overviewCard({
      title: e.gateway.displayName || e.gateway.hostname || gw,
      entityType: 'gateway',
      description: t('gateway.type'),
      clickable: true,
      actions: this.connButton(e),
      onClick: () => this.nav({ view: 'gateway', gatewayId: gw }),
      // AUTHORITATIVE tri-state status (same mapper as the controller cards):
      // connecting → yellow, connected → green, disconnected → gray. Plus the
      // path the data plane took (LAN vs private server) + its RTT, the box's
      // per-NIC IPs (IT/OT/WiFi/public), and PLC count.
      badges: html`${statusBadge(statusColor(connState), statusText(connState))}${this.connViaBadge(e)}${gatewayNetBadges(e.gateway)}${statusBadge('blue', t('gateway.plcCount', { count: e.controllers.all.length }))}`,
    });
  }

  // The ONE controller card — identical here, on the gateway detail page, and in
  // the tree-fed views. Self-contained (own nav + hover-prefetch + delete); we
  // hand it only what it needs: the controller, its gateway, its description, the
  // gateway's plugin types (for the manual-delete affordance), and the backend.
  private controllerCard(e: GatewayEntry, c: Controller) {
    const gw = e.gateway.deviceFingerprint;
    return html`<vitesse-controller-card
      .controller=${c}
      .gatewayId=${gw}
      .gatewayName=${gatewayLabel(e.gateway)}
      .description=${this.descriptions.get(gw)?.get(c.id) ?? ''}
      .pluginTypes=${e.pluginTypes.all}
      .backend=${this.backend}
    ></vitesse-controller-card>`;
  }

  // The ONE camera card — same element as the gateway detail. The flat home view
  // passes the gateway badge (so same-named cameras across gateways stay
  // distinguishable) + the shared media plane (its preview is the live feed,
  // visible-gated so an off-screen grid costs nothing).
  private cameraCard(e: GatewayEntry, c: Camera) {
    return html`<vitesse-camera-card
      .camera=${c}
      .gatewayId=${e.gateway.deviceFingerprint}
      .gatewayName=${gatewayLabel(e.gateway)}
      .cameraStreams=${this.cameraStreams}
      ?poster=${e.transport.transportInfo.path === 'cloud'}
    ></vitesse-camera-card>`;
  }

  private modelCard(e: GatewayEntry, m: ModelView) {
    const gw = e.gateway.deviceFingerprint;
    return modelOverviewCard(m, {
      onClick: () => this.nav({ view: 'model', gatewayId: gw, modelId: m.id }),
      onHover: () => e.models.prefetchSource(m.id),
      gatewayName: gatewayLabel(e.gateway),
    });
  }

  private dashboardCard(e: GatewayEntry, d: DashboardSummary) {
    // The self-contained card owns hover-prefetch, confirm-delete and duplicate.
    // gatewayName because this view is FLAT across gateways: two sites both
    // have a "Press line overview", and a card that does not say which box it
    // came from is ambiguous the moment they do.
    return html`<vitesse-dashboard-card
      .summary=${d}
      .manager=${e.dashboards}
      .gatewayId=${e.gateway.deviceFingerprint}
      .gatewayName=${gatewayLabel(e.gateway)}
      .connState=${this.effectiveConnState(e)}
    ></vitesse-dashboard-card>`;
  }

  private integrationCard(e: GatewayEntry, i: Integration) {
    // Self-contained card (owns its confirm-delete flow). The flat home view
    // shows the gateway badge so two same-named integrations stay distinguishable.
    return html`<vitesse-integration-card
      .integration=${i}
      .gatewayId=${e.gateway.deviceFingerprint}
      .gatewayName=${gatewayLabel(e.gateway)}
      .backend=${this.backend}
    ></vitesse-integration-card>`;
  }

  private grid() {
    const items: unknown[] = [];
    // A gateway card for EVERY org gateway (connected or not) — never a ghost,
    // never hidden. The org-owns-nothing case falls through to the empty state.
    if (this.tab === 'all' || this.tab === 'gateways') items.push(...this.gateways.map((e) => this.gatewayCard(e)));
    if (this.tab === 'all' || this.tab === 'controllers') items.push(...this.allControllers.map(({ e, c }) => this.controllerCard(e, c)));
    if (this.tab === 'all' || this.tab === 'cameras') items.push(...this.allCameras.map(({ e, c }) => this.cameraCard(e, c)));
    if (this.tab === 'all' || this.tab === 'models') items.push(...this.allModels.map(({ e, m }) => this.modelCard(e, m)));
    if (this.tab === 'all' || this.tab === 'dashboards') items.push(...this.allDashboards.map(({ e, d }) => this.dashboardCard(e, d)));
    if (this.tab === 'all' || this.tab === 'integrations') items.push(...this.allIntegrations.map(({ e, i }) => this.integrationCard(e, i)));
    if (items.length === 0) {
      // The "All" tab has no entity name to slot in — say "Nothing yet." rather
      // than "No All yet." Per-entity tabs keep "No <entity> yet."
      //
      // …unless they exist and are merely out of reach, in which case saying "No Dashboards yet" is
      // a claim about the org that is false. The count badge stays the number of items LISTED — a
      // badge that disagreed with the grid beneath it would be the same defect wearing a hat — so
      // this line is where the difference gets told.
      const hidden = this.tab === 'all' ? { count: 0, relays: [] } : this.hiddenOnDisconnected(this.tab);
      const msg = hidden.count
        ? t('home.emptyTabOffline', { count: hidden.count, tab: t(`nav.${this.tab}`), relays: hidden.relays.join(', ') })
        : this.tab === 'all'
          ? t('home.emptyAll')
          : t('home.emptyTab', { tab: t(`nav.${this.tab}`) });
      return html`<div class="home-empty-gateways">
        <span class="home-empty-hint">${msg}</span>
      </div>`;
    }
    return baseGrid(items, 3);
  }

  render() {
    const greeting = this.firstName ? t('home.greeting', { name: this.firstName }) : t('home.greetingNoName');
    return html`
      <div class="home-page">
        <div class="home-hero">
          <div class="home-greeting-row">
            <div class="home-greeting-text">
              <h1 class="home-greeting">${greeting}</h1>
              <p class="home-subtitle">${t('home.subtitle')}</p>
            </div>
          </div>

          <button class="home-prompt-bar">
            <vitesse-icon name="sparkles" size="18" class="home-prompt-icon"></vitesse-icon>
            <span class="home-prompt-text">${t('home.promptPlaceholder')}</span>
            <span class="home-prompt-voice-button" role="button" tabindex="0" title=${t('home.voiceTitle')} @click=${(e: Event) => e.stopPropagation()}>
              <vitesse-icon name="audio-lines" size="18"></vitesse-icon>
            </span>
          </button>

          <div class="prompt-chips home-prompt-chips-centered">
            ${CHIP_KEYS.map(
              (k) => html`<button class="prompt-chip">
                <vitesse-icon name="sparkles" size="12" class="prompt-chip-sparkle"></vitesse-icon>
                <span class="prompt-chip-text">${t(`home.${k}`)}</span>
              </button>`,
            )}
          </div>
        </div>

        ${this.claimBanner()}

        <div class="home-entity-section">
          <div class="entity-tab-header">
            ${pillTabBar({ tabs: this.tabs, active: this.tab, onChange: (id) => this.selectTab(id as HomeTab) })}
          </div>
          ${this.grid()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-home': VitesseHome;
  }
}
